import { useState } from "react";
import { redirect, useLoaderData, useNavigate } from "react-router-dom";

import {
  CURRENT_ORDER_QUERY_KEY,
  invalidateCurrentOrderDetails,
} from "@/modules/cart/useCartDetails";
import { waitObj } from "@/toolbox/Promise";
import { pget, post } from "api";
import { Models } from "api/types";
import ChevronLeft from "atoms/icon/icons/chevron_left.svg";
import { TermsAndCondition } from "atoms/terms-and-condition/TermsAndCondition";
import { QuantityAddToCart } from "components/QuantityAddToCart";
import useHistory from "hooks/useHistory";
import {
  generateShopUrl,
  RetailProductDetailsSection,
  RetailProductHeader,
  RetailProductImageSection,
  RetailProductPricingSection,
} from "modules/retailProducts";
import TopNav from "molecules/navbars/TopNav";
import { AddedToCartModal } from "organisms/added-to-cart-modal/AddedToCartModal";
import { ServiceFeeRow } from "organisms/order-summary/ServiceFeeRow";
import { queryClient } from "pages/Root";
import { loadIfFlag, useFeatureFlag } from "services/Flags";

const getShop = pget("/shop");
const getPlatform = pget("/platform");
const getUser = pget("/user");
const getProduct = pget(
  "/retailproducts/{retailProductListingId}/shop-product-listing-details"
);
const getBrands = pget("/retailproducts/{platformId}/default/brands");
const getDiscounts = pget("/shop/discounts");
const getCategories = pget("/shop/custom-categories/listings");
const getListings = pget("/listings");
const getMembershipDiscounts = pget("/memberships/discount-prices");

type LoaderData = Awaited<ReturnType<typeof productPDPLoader>>;
export const productPDPLoader = loadIfFlag("selling-products")(
  async ({ params: { id } }) => {
    if (!id) throw redirect("..");

    const platform = queryClient.ensureQueryData(getPlatform.keyed());
    const user = queryClient.ensureQueryData(getUser.keyed());
    const shopData = queryClient.ensureQueryData(getShop.keyed());

    const shopDiscounts = queryClient.ensureQueryData(getDiscounts.keyed());
    const categories = queryClient.ensureQueryData(getCategories.keyed());
    const listings = queryClient.ensureQueryData(getListings.keyed());

    const listing = queryClient.fetchQuery(
      getProduct.keyed({ retailProductListingId: id })
    );

    const brands = queryClient.ensureQueryData(
      getBrands.keyed({ platformId: (await platform).id })
    );

    const membershipDiscounts = queryClient.fetchQuery({
      ...getMembershipDiscounts.keyed(),
    });

    return waitObj({
      shopData,
      platform,
      listing,
      user,
      brands,
      shopDiscounts,
      categories,
      listings,
      membershipDiscounts,
    });
  }
);

const AddToCartModal = (props: {
  onClose: () => void;
  price: Models<"DisplayRetailProductQuantityPrice">;
  listing: Models<"DisplayShopRetailProductListingDetails">;
}) => {
  return (
    <AddedToCartModal
      title={props.listing.name.text}
      open
      subtitle={`${props.price.quantity} ${
        props.price.quantity != 1
          ? props.listing.unitType.pluralUnitType || "items"
          : props.listing.unitType.singularUnitType || "item"
      }`}
      cost={props.price.priceInCents}
      membership={null}
      onClose={props.onClose}
    />
  );
};

const ProductPDP = () => {
  const { routeBackNumber } = useHistory();
  const navigate = useNavigate();
  const isUnitedDerm = useFeatureFlag("unitedDerm");
  const {
    brands,
    shopData,
    platform,
    listing,
    user,
    shopDiscounts,
    categories,
    listings,
    membershipDiscounts,
  } = useLoaderData() as LoaderData;

  type Modal = { modal: "added-to-cart"; priceId: string };
  const [showModal, setShowModal] = useState<Modal | null>(null);

  const packages = listing.shopRetailProductVariants;

  const [selectedPackageId, setSelectedPackageId] = useState(packages[0].id);

  const affirmEnabled = !!platform.platformSettings?.affirmEnabled;
  const brand = brands.find((brand) => brand.id === listing.taxonomizedBrandId);

  const productDiscount = shopDiscounts.listingDiscounts[listing.id];

  const addToCart = async (selectedPriceId: string) => {
    try {
      await post("/orders/current/lineitems", {
        packageListingId: listing.id,
        packageId: selectedPackageId,
        packagePriceId: selectedPriceId,
        membershipId: null,
      });

      await invalidateCurrentOrderDetails();

      setShowModal({ modal: "added-to-cart", priceId: selectedPriceId });
    } catch (error) {
      console.error("Failed to add product to cart:", error);
    }
  };

  const selectedPackage = packages.find((pkg) => pkg.id === selectedPackageId);

  // These should all be guaranteed to exist
  if (!listing || !brand || !selectedPackage) {
    throw new Error("Product or brand not found");
  }

  const prices = selectedPackage.displayRetailProductQuantityPrices;

  const membershipDiscount = membershipDiscounts.find(
    (md) => md.packageId === selectedPackageId
  );

  return (
    <div className="flex h-full min-h-screen flex-col">
      <TopNav
        startIconSVG={ChevronLeft}
        onStartIconClick={() => navigate(routeBackNumber)}
      >
        <div className="flex max-h-[32px] items-center">
          <h1 className="text-xl">Products</h1>
        </div>
      </TopNav>

      <div className="flex flex-1 flex-col overflow-y-auto bg-white">
        <RetailProductHeader
          productBrand={brand.name.text}
          productName={listing.name.text}
          isFeaturedProduct={listing.isFeaturedProduct}
          onBrandClick={() =>
            navigate(
              generateShopUrl("browse-by-brand", {
                brands: listing.taxonomizedBrandId,
              })
            )
          }
        />

        <div className="relative z-0">
          <RetailProductImageSection
            imageUris={listing.imageUris}
            productName={listing.name.text}
          />
        </div>

        <div className="border-grey border-b p-4">
          <RetailProductPricingSection
            oneTimePurchasePriceInCents={prices[0].priceInCents}
            memberPriceInCents={membershipDiscount?.discountedAmountInCents}
            packageType={
              listing.type === 1 ? "Product" : "ProductFromExternalSource"
            }
            affirmEnabled={affirmEnabled}
            isGuest={user.isGuest}
            productDiscount={productDiscount}
            productVariants={packages}
            selectedVariantId={selectedPackageId}
            onVariantSelect={setSelectedPackageId}
            listings={listings}
            customShopCategories={categories}
            shippingSubsidyMinAmountInCents={
              platform.platformSettings
                ?.skinDropShippingSubsidyMinOrderAmountInCents
            }
            productName={listing.name.text}
          />
        </div>

        <div className="px-2 pb-4">
          <RetailProductDetailsSection
            keyBenefits={listing.keyBenefits.map((benefit) => ({
              text: benefit.text,
            }))}
            description={listing.description}
            areas={shopData.areas ?? []}
            concerns={shopData.concerns || []}
            areaIds={listing.bodyAreaIds}
            concernIds={listing.healthConcernIds}
            defaultIngredients={listing.shopRetailProductVariants[0].ingredients.map(
              (ingredient) => ingredient
            )}
          />
        </div>

        {/* Terms and Conditions */}
        <div className="px-4 pt-4">
          <TermsAndCondition
            hideText={!platform.platformSettings?.affirmEnabled}
          />
        </div>

        {/* Convenience Fee Notice */}
        {platform.platformSettings?.patientFeePercentage != null &&
          platform.platformSettings.patientFeePercentage > 0 && (
            <div className="px-4 pb-4">
              <ServiceFeeRow
                title={
                  !isUnitedDerm
                    ? "Convenience fee applied at checkout"
                    : "Credit card fee applied at checkout"
                }
              />
            </div>
          )}
      </div>

      <div className="sticky bottom-[calc(59px_+_env(safe-area-inset-bottom)-1px)] w-full bg-white p-4 shadow-lg">
        <QuantityAddToCart
          prices={selectedPackage.displayRetailProductQuantityPrices}
          isAvailable={
            listing.listingStatus === "OnMarketplace" &&
            !selectedPackage.isOutOfStock
          }
          isGuest={user.isGuest}
          onAddToCart={addToCart}
          guestButtonLabel="Sign up to add to cart"
          availableButtonLabel="Add to Cart"
          unavailableButtonLabel="Out of Stock"
        />
      </div>

      {showModal?.modal === "added-to-cart" && (
        <AddToCartModal
          onClose={() => setShowModal(null)}
          price={
            prices.find((price) => price.priceId === showModal.priceId) ??
            prices[0]
          }
          listing={listing}
        />
      )}
    </div>
  );
};

export default ProductPDP;
